import { useQuery } from '@tanstack/vue-query'
import type { Ref } from 'vue'
import { computed, ref } from 'vue'
import { useRpQuery } from '~/composables/graphql'
import { graphql } from '~/resources/graphql'
import type { ContractPropertyInput } from '~/resources/graphql/graphql'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useSelectedLanguage } from '~/composables/use-selected-language'
import {
    type AppliedPromotionalCode,
    getForInput
} from '~/components/PurchaseContract/promotional-code'
import { useViewer } from '~/composables/use-login'

export function usePreviewSessionContractQuery(
    sessionId: Ref<string | null>,
    startDateLocal: Ref<string | null>,
    enrollmentTypeCounts: Ref<
        {
            enrollmentTypeId: string
            count: number
        }[]
    >,
    promotionalCodes: Ref<AppliedPromotionalCode[]>,
    propertiesInput?: Ref<ContractPropertyInput[]>,
    pricingKey?: Ref<number>
) {
    const activeFacility = useActiveFacility()
    const viewer = useViewer()
    const query = useRpQuery()
    const customerId = computed(() => {
        return viewer.value?.customerId ?? null
    })

    const selectedLanguage = useSelectedLanguage()
    const { data, isFetching } = useQuery({
        queryKey: [
            'PreviewSessionContractQuery',
            sessionId,
            startDateLocal,
            enrollmentTypeCounts,
            pricingKey ?? ref(0),
            activeFacility,
            promotionalCodes,
            customerId,
            selectedLanguage
        ],
        placeholderData: previousData => previousData,
        queryFn: () => {
            if (sessionId.value === null || startDateLocal.value === null) {
                return null
            }

            return query(
                graphql(/* GraphQL */ `
                    query PreviewSessionContractQuery(
                        $sessionId: ID!
                        $input: PreviewContractInput!
                        $language: Language!
                    ) {
                        session(id: $sessionId) {
                            previewContract(input: $input) {
                                cart {
                                    ...PreviewSessionPricingFragment
                                    subtotal
                                    total
                                }
                                duesPricing {
                                    ...DuesPricingDisplayDuesPricingPreviewItemFragment
                                }
                                nextBillDate
                                paymentDueMode
                                depositAmount
                            }
                        }
                    }
                `),
                {
                    sessionId: sessionId.value,
                    input: {
                        customerId: customerId.value,
                        startDate: startDateLocal.value,
                        enrollmentTypeCounts: enrollmentTypeCounts.value,
                        properties: propertiesInput?.value,
                        promotionCodes: getForInput(promotionalCodes.value)
                    },
                    language: selectedLanguage.value
                }
            )
        }
    })

    const previewContract = computed(() => {
        if (data.value === undefined) {
            return undefined
        }

        // No start date
        if (data.value === null) {
            return undefined
        }

        if (data.value.session === null) {
            throw new Error()
        }

        if (data.value.session.previewContract === null) {
            return null
        }

        return data.value.session.previewContract
    })

    const cart = computed(() => {
        if (previewContract.value === undefined) {
            return undefined
        }

        if (previewContract.value === null) {
            return null
        }

        return previewContract.value.cart
    })

    const duesPricing = computed(() => {
        if (previewContract.value === undefined) {
            return undefined
        }

        if (previewContract.value === null) {
            return null
        }

        return previewContract.value.duesPricing
    })

    const nextBillDate = computed(() => {
        if (previewContract.value === undefined) {
            return undefined
        }

        if (previewContract.value === null) {
            return null
        }

        return previewContract.value.nextBillDate
    })

    const paymentDueMode = computed(() => {
        if (previewContract.value === undefined) {
            return undefined
        }

        if (previewContract.value === null) {
            return null
        }

        return previewContract.value.paymentDueMode
    })

    const depositAmount = computed(() => {
        if (previewContract.value === undefined) {
            return undefined
        }

        if (previewContract.value === null) {
            return null
        }

        return previewContract.value.depositAmount
    })

    const subtotal = computed(() => cart.value?.subtotal)
    const total = computed(() => cart.value?.total)

    return {
        cart,
        duesPricing,
        nextBillDate,
        paymentDueMode,
        depositAmount,
        isFetching,
        subtotal,
        total
    }
}
